import { Badge } from 'src/design/atoms/Badge';
import { LoadingOverlay } from 'src/design/atoms/LoadingOverlay';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Drawer } from 'src/design/molecules/Drawer';
import { Table } from 'src/design/molecules/Table';
import { TextInput } from 'src/design/atoms/TextInput';
import { IconSearch } from '@tabler/icons-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useAzureGroupSyncMemberships } from 'src/api/queries/admin/useAzureGroupSync';
import { formatDateTime } from 'src/utils';

interface AzureGroupSyncMembershipsDrawerProps {
  opened: boolean;
  onClose: () => void;
}

const syncStatusColor: Record<string, string> = {
  synced: 'green',
  error: 'red',
  pending: 'yellow',
  removed: 'gray',
};

export const AzureGroupSyncMembershipsDrawer: React.FC<
  AzureGroupSyncMembershipsDrawerProps
> = ({ opened, onClose }) => {
  const [filter, setFilter] = useState('');
  const membershipsQuery = useAzureGroupSyncMemberships(opened);
  const memberships = useMemo(
    () => membershipsQuery.data ?? [],
    [membershipsQuery.data],
  );
  const loading = membershipsQuery.isFetching;

  useEffect(() => {
    if (opened) setFilter('');
  }, [opened]);

  const filtered = useMemo(() => {
    if (!filter.trim()) return memberships;
    const q = filter.toLowerCase();
    return memberships.filter(
      (m) =>
        m.userName?.toLowerCase().includes(q) ||
        m.userEmail?.toLowerCase().includes(q) ||
        m.azureGroupDisplayName?.toLowerCase().includes(q) ||
        m.azureGroupId?.toLowerCase().includes(q) ||
        m.syncStatus?.toLowerCase().includes(q),
    );
  }, [memberships, filter]);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title="Azure Group Memberships"
      position="right"
      size="70%"
    >
      <Stack pos="relative" mih={200}>
        <LoadingOverlay visible={loading} />
        {memberships.length > 0 && (
          <TextInput
            placeholder="Filter by name, email, group, or status..."
            leftSection={<IconSearch size={16} />}
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
          />
        )}
        {filtered.length === 0 && !loading && (
          <Text c="dimmed" ta="center" mt="lg">
            {memberships.length > 0
              ? 'No matching memberships.'
              : 'No memberships found.'}
          </Text>
        )}
        {filtered.length > 0 && (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>User</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Group</Table.Th>
                <Table.Th>Sync Status</Table.Th>
                <Table.Th>Last Synced</Table.Th>
                <Table.Th>Last Error</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.map((m) => (
                <Table.Tr key={m.id}>
                  <Table.Td>{m.userName || '—'}</Table.Td>
                  <Table.Td>{m.userEmail || '—'}</Table.Td>
                  <Table.Td>
                    {m.azureGroupDisplayName ? (
                      <>
                        <Text size="sm">{m.azureGroupDisplayName}</Text>
                        <Text size="xs" c="dimmed" ff="monospace">
                          {m.azureGroupId}
                        </Text>
                      </>
                    ) : (
                      <Text size="xs" ff="monospace">
                        {m.azureGroupId}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Badge color={syncStatusColor[m.syncStatus] ?? 'gray'}>
                      {m.syncStatus}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {m.lastSyncedAt ? formatDateTime(m.lastSyncedAt) : '—'}
                  </Table.Td>
                  <Table.Td>
                    {m.lastError && (
                      <Text size="xs" c="red" lineClamp={2}>
                        {m.lastError}
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Drawer>
  );
};
